// ---------------------------------------------------------------------------
// TeamGrid Renderer (editor key: TeamGrid)
// ---------------------------------------------------------------------------
// Renders a grid of team member cards. Editor input is a pipe-delimited
// textarea ("Name | Role | Photo URL | Profile URL | Bio" per line). Photo
// sources route through the `imageUrl` safelist so `javascript:` and inline
// SVG data URIs collapse to `#`; profile links use the standard URL
// safelist. Column count is clamped 1-4. Markup is a `<section>` with an
// inner grid of `<article>` cards — pure static HTML, no JS required.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, imageUrl, text, url } from "../escape.js";

interface TeamGridProps {
  heading?: string;
  members?: string;
  columns?: number | string;
}

interface MemberEntry {
  name: string;
  role: string;
  photo: string;
  profile: string;
  bio: string;
}

function parseMembers(value: unknown): MemberEntry[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const parts = line.split("|");
      return {
        name: (parts[0] ?? "").trim(),
        role: (parts[1] ?? "").trim(),
        photo: (parts[2] ?? "").trim(),
        profile: (parts[3] ?? "").trim(),
        bio: (parts[4] ?? "").trim(),
      };
    })
    .filter((entry) => entry.name.length > 0);
}

function clampColumns(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (Number.isNaN(parsed)) return 3;
  return Math.max(1, Math.min(4, Math.floor(parsed)));
}

function renderCard(entry: MemberEntry): string {
  const photoHtml = entry.photo
    ? `\n      <img class="ease-team-grid__photo" src="${imageUrl(entry.photo)}" alt="${attr(entry.name)}" loading="lazy" />`
    : "";
  const roleHtml = entry.role
    ? `\n      <p class="ease-team-grid__role">${text(entry.role)}</p>`
    : "";
  const bioHtml = entry.bio
    ? `\n      <p class="ease-team-grid__bio">${text(entry.bio)}</p>`
    : "";

  const nameNode = entry.profile
    ? `<a class="ease-team-grid__name-link" href="${url(entry.profile)}">${text(entry.name)}</a>`
    : text(entry.name);

  return `    <article class="ease-team-grid__item">${photoHtml}
      <h3 class="ease-team-grid__name">${nameNode}</h3>${roleHtml}${bioHtml}
    </article>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as TeamGridProps;

  const members = parseMembers(p.members);
  if (members.length === 0) {
    return "<!-- team-grid missing members -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const columns = clampColumns(p.columns);

  const headingHtml = heading
    ? `\n  <h2 class="ease-team-grid__heading">${text(heading)}</h2>`
    : "";

  const cards = members.map(renderCard).join("\n");

  return `<section class="ease-section ease-team-grid ease-team-grid--cols-${columns}" data-ease-columns="${attr(String(columns))}">${headingHtml}
  <div class="ease-team-grid__grid" role="list">
${cards}
  </div>
</section>`;
}

registerRenderer("team-grid", { render });
